@for (conflict of conflicts(); track conflict.groupId + ':' + conflict.kind) {
  <div class="conflict-row" [class.has-state-mismatch]="conflict.kind === 'sa-address' && conflict.stateMismatch">
    <div class="conflict-header">
      <i class="material-symbols-rounded">{{ conflict.kind === 'sa-address' && conflict.stateMismatch ? 'block' : 'priority_high' }}</i>
      <span class="conflict-title">
        @if (conflict.kind === 'sa-address' && conflict.stateMismatch) {
          State doesn't match
        } @else if (conflict.kind === 'len-name') {
          Legal Entity name doesn't match
        } @else {
          Service Address doesn't match
        }
      </span>
    </div>

    @if (conflict.kind === 'sa-address' && conflict.stateMismatch) {
      <!-- State-mismatch hard-stop. No Change/Use buttons offered. -->
      <div class="state-mismatch-block">
        <p class="state-mismatch-desc">
          The bill's state (<strong>{{ conflict.extracted.state || '—' }}</strong>) doesn't match the existing service address state (<strong>{{ conflict.parent.state }}</strong>).
          State can't be changed on an existing address &mdash; utility and meter linkage depend on it.
        </p>
        <p class="state-mismatch-action">
          <strong>Re-import at the Customer or Legal Entity level</strong> to create a new service address with the correct state.
        </p>
      </div>
    } @else if (conflict.kind === 'len-name') {
      <div class="conflict-body">
        <div class="value-row">
          <span class="label">Existing:</span>
          <span class="value">{{ conflict.parent }}</span>
        </div>
        <mat-form-field appearance="outline" class="len-name-field">
          <mat-label>From bill (edit to resolve)</mat-label>
          <input matInput
                 [value]="lenDraftMap()[conflict.groupId]?.name"
                 (input)="onLenNameInput(conflict.groupId, $any($event.target).value)"
                 data-testid="len-name-input" />
        </mat-form-field>
        <div class="conflict-actions">
          <button class="btn-secondary" (click)="startUseExisting(conflict.groupId)" data-testid="use-existing-btn">
            Use Existing
          </button>
          <button class="btn-primary" (click)="startChangeExisting(conflict.groupId)" data-testid="change-existing-btn">
            Change Existing
          </button>
        </div>
      </div>
    } @else {
      <div class="conflict-body sa-editor">
        <div class="value-row">
          <span class="label">Existing:</span>
          <span class="value">
            {{ conflict.parent.street1 }}, {{ conflict.parent.city }}, {{ conflict.parent.state }} {{ conflict.parent.zip }}
          </span>
        </div>
        <div class="value-row">
          <span class="label">From bill:</span>
          <span class="value">
            {{ conflict.extracted.street1 }}, {{ conflict.extracted.city }}, {{ conflict.extracted.state }} {{ conflict.extracted.zip }}
          </span>
        </div>
        <mat-form-field appearance="outline">
          <mat-label>Street 1</mat-label>
          <input matInput
                 [value]="saDraftMap()[conflict.groupId]?.street1"
                 (input)="onSaFieldInput(conflict.groupId, 'street1', $any($event.target).value)"
                 data-testid="sa-street1-input" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Street 2</mat-label>
          <input matInput
                 [value]="saDraftMap()[conflict.groupId]?.street2"
                 (input)="onSaFieldInput(conflict.groupId, 'street2', $any($event.target).value)"
                 data-testid="sa-street2-input" />
        </mat-form-field>
        <div class="three-col">
          <mat-form-field appearance="outline">
            <mat-label>City</mat-label>
            <input matInput
                   [value]="saDraftMap()[conflict.groupId]?.city"
                   (input)="onSaFieldInput(conflict.groupId, 'city', $any($event.target).value)"
                   data-testid="sa-city-input" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="state-field">
            <mat-label>
              State
              <i class="material-symbols-rounded help-icon"
                 matTooltip="State can't be changed here. States are critical to utility/meter linkage. Edit the service address directly outside this flow.">help</i>
            </mat-label>
            <mat-select [value]="saDraftMap()[conflict.groupId]?.state" disabled data-testid="sa-state-select">
              @for (s of stateOptions; track s) {
                <mat-option [value]="s">{{ s }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>ZIP</mat-label>
            <input matInput
                   [value]="saDraftMap()[conflict.groupId]?.zip"
                   (input)="onSaFieldInput(conflict.groupId, 'zip', $any($event.target).value)"
                   data-testid="sa-zip-input" />
          </mat-form-field>
        </div>
        <div class="conflict-actions">
          <button class="btn-secondary" (click)="startUseExisting(conflict.groupId)" data-testid="use-existing-btn">
            Use Existing
          </button>
          <button class="btn-primary" (click)="startChangeExisting(conflict.groupId)" data-testid="change-existing-btn">
            Change Existing
          </button>
        </div>
      </div>
    }

    <!-- Use Existing confirmation prompt -->
    @if (isConfirmingUseExisting(conflict.groupId)) {
      <div class="confirm-overlay" (click)="cancelUseExisting()">
        <div class="confirm-dialog" (click)="$event.stopPropagation()">
          <h4>Use Existing</h4>
          <p>Update the imported {{ conflict.kind === 'len-name' ? 'legal entity' : 'service address' }} to match the existing one?</p>
          <div class="confirm-actions">
            <button class="btn-secondary" (click)="cancelUseExisting()">Cancel</button>
            <button class="btn-primary" (click)="confirmUseExisting(conflict)" data-testid="use-existing-confirm-btn">
              Update Imported
            </button>
          </div>
        </div>
      </div>
    }

    <!-- Change Existing confirmation modal -->
    @if (isChangeExistingActive(conflict.groupId)) {
      <div class="confirm-overlay" (click)="cancelChangeExisting()">
        <div class="confirm-dialog" (click)="$event.stopPropagation()">
          <h4>Change Existing</h4>
          <p>You're about to modify the original {{ conflict.kind === 'len-name' ? 'legal entity' : 'service address' }}. <strong>This is not undoable from this workflow.</strong></p>

          @if (changeExistingStatus() === 'error') {
            <div class="confirm-error">
              <i class="material-symbols-rounded">error</i>
              <span>{{ changeExistingError() }}</span>
            </div>
          }

          <div class="confirm-actions">
            <button class="btn-secondary"
                    [disabled]="changeExistingStatus() === 'pending'"
                    (click)="cancelChangeExisting()">
              Cancel
            </button>
            @if (changeExistingStatus() === 'error') {
              <button class="btn-primary"
                      (click)="confirmChangeExisting(conflict)"
                      data-testid="change-existing-retry-btn">
                Retry
              </button>
            } @else {
              <button class="btn-primary"
                      [disabled]="changeExistingStatus() === 'pending'"
                      (click)="confirmChangeExisting(conflict)"
                      data-testid="change-existing-confirm-btn">
                {{ changeExistingStatus() === 'pending' ? 'Updating…' : 'Modify Original' }}
              </button>
            }
          </div>
        </div>
      </div>
    }
  </div>
}
